import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useEstablishments } from "@/context/EstablishmentContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PRIORITY_META, scoreColor } from "@/lib/helpers";
import { ArrowLeft, CheckCircle2, AlertTriangle, Info, HelpCircle, Gauge } from "lucide-react";

export default function PillarDetail() {
  const { key } = useParams();
  const { current } = useEstablishments();
  const navigate = useNavigate();
  const [pillar, setPillar] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/establishments/${current.id}/dashboard`);
      const p = data.latest_audit?.pillars?.find((x) => x.key === key);
      setPillar(p || null);
    } finally { setLoading(false); }
  }, [current, key]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 grid place-items-center min-h-[60vh]"><Gauge className="w-8 h-8 text-blue-600 animate-spin" /></div>;
  if (!pillar) return <div className="p-8 text-slate-500">Pilier introuvable. <Button variant="link" onClick={() => navigate("/app/dashboard")}>Retour</Button></div>;

  const meta = PRIORITY_META[pillar.priority] || PRIORITY_META.NON_MESURE;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      <button onClick={() => navigate("/app/dashboard")} data-testid="pillar-back-button" className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
      </button>

      <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">{pillar.name}</h1>
            <p className="text-slate-500 text-sm mt-1">{pillar.measured_count} point(s) mesuré(s) à partir de données réelles</p>
            {pillar.score == null && <p className="mt-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="pillar-unmeasured-hint">Pilier à activer — {pillar.hint}</p>}
            {pillar.score != null && pillar.pending_checks?.length > 0 && <p className="mt-2 text-xs text-slate-500" data-testid="pillar-pending-hint">{pillar.pending_checks.length} point(s) supplémentaire(s) pourront être mesurés après « Analyser mon site » / connexion Google.</p>}
          </div>
          <div className="text-right">
            <div className={`font-heading text-4xl font-extrabold ${scoreColor(pillar.score)}`}>{pillar.score == null ? "—" : pillar.score}</div>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.text}`}>{pillar.score == null ? "Non mesuré" : meta.label}</span>
          </div>
        </div>
        {pillar.strengths?.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {pillar.strengths.map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" /> {s}</span>
            ))}
          </div>
        )}
      </div>

      <h2 className="font-heading text-lg font-semibold mb-3">Analyse détaillée</h2>
      <div className="space-y-4">
        {pillar.checks.filter((c) => c.client_visible !== false).map((c) => {
          const cm = PRIORITY_META[c.priority] || PRIORITY_META.NON_MESURE;
          return (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`check-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-medium text-slate-800">{c.label}</h3>
                <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${cm.bg} ${cm.text}`}>{cm.label}</span>
              </div>

              {/* DONNÉE MESURÉE */}
              <div className={`mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${c.available ? "bg-slate-50 text-slate-700" : "bg-slate-100 text-slate-400"}`}>
                {c.available ? <Info className="w-4 h-4 mt-0.5 text-blue-500 shrink-0" /> : <HelpCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                <span><b className="font-semibold text-xs uppercase tracking-wide mr-1">Mesuré :</b>{c.measured}</span>
              </div>

              {/* ANALYSE */}
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 mr-1">Ce que ça veut dire :</span>{c.explanation}
              </p>

              {/* RECOMMANDATION */}
              {c.available && c.score < 75 && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
                  <div>
                    <p className="text-sm text-blue-900"><b>Recommandation :</b> {c.recommendation}</p>
                    <div className="text-xs text-blue-700/70 mt-1">Difficulté : {c.difficulty} · Temps estimé : {c.time}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
